// client/src/components/Card.jsx
import React from 'react';
import { motion } from 'framer-motion';
import AnimeAvatar from './AnimeAvatar';

const Card = ({ player, isSelected, onSelect, disabled, className = '' }) => {
  // إعدادات الألوان حسب الندرة (ذهبي، فضي، برونزي، رمادي للعادي)
  const rarityConfig = {
    gold: {
      border: 'from-yellow-400 to-yellow-600',
      bgGradient: 'from-yellow-500/20 to-yellow-700/10',
      powerBar: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
    },
    silver: {
      border: 'from-gray-300 to-gray-500',
      bgGradient: 'from-gray-400/20 to-gray-600/10',
      powerBar: 'bg-gradient-to-r from-gray-300 to-gray-500',
    },
    bronze: {
      border: 'from-amber-600 to-amber-800',
      bgGradient: 'from-amber-600/20 to-amber-800/10',
      powerBar: 'bg-gradient-to-r from-amber-500 to-amber-700',
    },
    common: {
      border: 'from-gray-500 to-gray-700',
      bgGradient: 'from-gray-500/20 to-gray-700/10',
      powerBar: 'bg-gradient-to-r from-gray-500 to-gray-700',
    },
  };

  const config = rarityConfig[player.rarity] || rarityConfig.common;

  const getShortAbility = (ability) => {
    if (!ability) return null;
    const map = {
      Playmaker: '✨ Playmaker',
      'Power Builder': '💪 Builder',
      'Super Scorer': '🎯 Scorer+',
      'Penalty Magnet': '⚡ Magnet',
      'Super Saver': '🧤 Saver+',
      'Speed Runner': '🏃 Speed+',
      'Clean Man': '🧼 Clean',
    };
    return map[ability] || ability;
  };

  const shortAbility = getShortAbility(player.special_ability);

  // حساب القوة الكلية للاعب باستخدام جميع الصفات الست
  const power = Math.round(
    (Number(player.attack) + Number(player.defense) + Number(player.speed) + Number(player.accuracy) +
     (player.strength || 50) + (player.tactics || 50)) / 6
  );

  return (
    <motion.div
      className={`relative w-60 h-[26rem] rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 
        ${disabled ? 'opacity-60 cursor-default' : 'hover:scale-105 hover:-translate-y-1'}
        ${isSelected ? 'ring-4 ring-yellow-400 shadow-xl' : ''}
        ${className}`}
      whileHover={!disabled ? { scale: 1.02, y: -5 } : {}}
      onClick={() => !disabled && onSelect(player)}
    >
      {/* الخلفية المتدرجة حسب الندرة */}
      <div className={`absolute inset-0 bg-gradient-to-br ${config.bgGradient} backdrop-blur-sm`} />
      
      {/* الإطار الخارجي المتدرج */}
      <div className={`absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-br ${config.border} opacity-80`} />
      
      {/* علامة الاختيار (عند التحديد) */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg z-20">
          <span className="text-black text-sm font-bold">✓</span>
        </div>
      )}

      {/* الصورة الرمزية للاعب */}
      <div className="relative z-10 flex justify-center mt-6">
        <AnimeAvatar position={player.position} rarity={player.rarity} className="w-28 h-28" />
      </div>

      {/* اسم اللاعب والمركز */}
      <div className="relative z-10 text-center mt-2">
        <h3 className="text-white font-bold text-lg tracking-wide">{player.name}</h3>
        <p className="text-gray-300 text-sm font-medium">{player.position}</p>
      </div>

      {/* القدرة الخاصة (إن وجدت) */}
      {shortAbility && (
        <div className="relative z-10 mt-2 mx-auto w-fit px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-yellow-300 text-xs font-semibold flex items-center gap-1">
          <span className="text-yellow-400">✨</span> {shortAbility}
        </div>
      )}

      {/* الصف الأول من الإحصائيات (الهجوم، الدفاع، السرعة، الدقة) */}
      <div className="relative z-10 mt-3 w-full px-3 grid grid-cols-4 gap-1 text-center">
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400">ATK</div>
          <div className="text-white font-bold text-sm">{player.attack}</div>
        </div>
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400">DEF</div>
          <div className="text-white font-bold text-sm">{player.defense}</div>
        </div>
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400">SPD</div>
          <div className="text-white font-bold text-sm">{player.speed}</div>
        </div>
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400">ACC</div>
          <div className="text-white font-bold text-sm">{player.accuracy}</div>
        </div>
      </div>

      {/* الصف الثاني من الإحصائيات (القوة البدنية، التكتيك) */}
      <div className="relative z-10 mt-2 w-full px-3 grid grid-cols-2 gap-1 text-center">
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400 flex items-center justify-center gap-1">💪 STR</div>
          <div className="text-white font-bold text-sm">{player.strength !== undefined ? player.strength : 50}</div>
        </div>
        <div className="bg-black/50 rounded-lg py-1">
          <div className="text-[10px] text-gray-400 flex items-center justify-center gap-1">🧠 TAC</div>
          <div className="text-white font-bold text-sm">{player.tactics !== undefined ? player.tactics : 50}</div>
        </div>
      </div>

      {/* شريط قوة اللاعب */}
      <div className="relative z-10 mt-3 w-full px-3">
        <div className="flex justify-between items-center text-xs text-gray-300 mb-1">
          <span>⚡ POWER</span>
          <span className="font-mono font-bold">{power}</span>
        </div>
        <div className="w-full bg-black/50 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-full rounded-full ${config.powerBar} shadow-md`}
            style={{ width: `${power}%` }}
          />
        </div>
      </div>

      {/* شارة الكابتن */}
      {player.is_captain && (
        <div className="absolute bottom-2 right-2 bg-yellow-500/80 rounded-full p-1 shadow-lg z-20">
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12l-5 3 2-6-5-4h6l2-5 2 5h6l-5 4 2 6z" />
          </svg>
        </div>
      )}
    </motion.div>
  );
};

export default Card;